import React, { useState } from 'react';
import { ComposableMap, Geographies, Geography, Marker, Line } from 'react-simple-maps';
import { useLanguage } from '../context/LanguageContext';
import { useInView } from '../hooks/useInView';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json';

const pins = [
  { coordinates: [-97.5044, 25.8697], isHQ: true },   // Matamoros
  { coordinates: [-97.4975, 25.9017], isHQ: false },   // Brownsville
  { coordinates: [-105.7821, 39.5501], isHQ: false },   // Colorado
  { coordinates: [-102.5528, 23.6345], isHQ: false },   // Mexico (center)
  { coordinates: [-74.2973, 4.5709], isHQ: false },     // Colombia (Bogota)
  { coordinates: [-76.0199, -9.1900], isHQ: false },    // Peru (center)
];

const HQ = pins[0].coordinates;

export default function GeographicReach() {
  const { t } = useLanguage();
  const [ref, isInView] = useInView();
  const [activePin, setActivePin] = useState(null);

  return (
    <section
      id="reach"
      ref={ref}
      data-testid="reach-section"
      className="py-20 bg-[#16213E] relative overflow-hidden"
    >
      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Section label */}
        <div className="mb-4">
          <span className="text-[#E71C25] text-xs font-bold tracking-[0.3em] uppercase">
            {t.nav.reach}
          </span>
        </div>

        <h2
          className={`text-3xl lg:text-4xl font-bold mb-3 tracking-tight transition-all duration-700 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {t.reach.heading}
        </h2>
        <p
          className={`text-[#B0B0B0] text-lg mb-10 transition-all duration-700 delay-100 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {t.reach.subtitle}
        </p>

        <div className="grid lg:grid-cols-5 gap-8 items-start">
          {/* Map - takes 3 columns */}
          <div
            className={`lg:col-span-3 relative rounded-lg overflow-hidden border border-white/5 bg-[#1A1A2E] transition-all duration-700 delay-200 ${
              isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <ComposableMap
              projection="geoAzimuthalEqualArea"
              projectionConfig={{
                rotate: [90, -15, 0],
                center: [0, 0],
                scale: 500,
              }}
              style={{ width: '100%', height: 'auto' }}
            >
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography
                      key={geo.rpiKey || geo.id || geo.properties?.name}
                      geography={geo}
                      fill="#0F1629"
                      stroke="rgba(255,255,255,0.06)"
                      strokeWidth={0.5}
                      style={{
                        default: { outline: 'none' },
                        hover: { outline: 'none', fill: '#162040' },
                        pressed: { outline: 'none' },
                      }}
                    />
                  ))
                }
              </Geographies>

              {/* Connection lines from HQ */}
              {pins.map((pin, i) =>
                i === 0 ? null : (
                  <Line
                    key={`line-${i}`}
                    from={HQ}
                    to={pin.coordinates}
                    stroke="#E71C25"
                    strokeWidth={1}
                    strokeOpacity={0.25}
                    strokeDasharray="4 4"
                    strokeLinecap="round"
                  />
                )
              )}

              {/* Location markers */}
              {pins.map((pin, i) => (
                <Marker
                  key={`pin-${i}`}
                  coordinates={pin.coordinates}
                  onMouseEnter={() => setActivePin(i)}
                  onMouseLeave={() => setActivePin(null)}
                >
                  {pin.isHQ ? (
                    <>
                      <circle r={12} fill="#E71C25" opacity={0.15}>
                        <animate attributeName="r" values="10;16;10" dur="2.5s" repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0.2;0.05;0.2" dur="2.5s" repeatCount="indefinite" />
                      </circle>
                      <circle r={6} fill="#E71C25" stroke="#fff" strokeWidth={1.5} />
                    </>
                  ) : (
                    <circle
                      r={activePin === i ? 5 : 4}
                      fill="white"
                      opacity={activePin === i ? 1 : 0.7}
                      stroke="#E71C25"
                      strokeWidth={activePin === i ? 1.5 : 0}
                      style={{ transition: 'all 0.2s' }}
                    />
                  )}
                  {/* Label on hover or for HQ */}
                  {(activePin === i || pin.isHQ) && (
                    <text
                      textAnchor="middle"
                      y={pin.isHQ ? -16 : -12}
                      style={{
                        fontFamily: 'Archivo, sans-serif',
                        fill: pin.isHQ ? '#E71C25' : 'white',
                        fontSize: pin.isHQ ? '10px' : '9px',
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {t.reach.locations[i]?.name}
                    </text>
                  )}
                </Marker>
              ))}
            </ComposableMap>
          </div>

          {/* Location list - takes 2 columns */}
          <div className="lg:col-span-2 space-y-3">
            {t.reach.locations.map((loc, i) => (
              <div
                key={i}
                data-testid={`reach-location-${i}`}
                className={`flex items-center gap-4 p-4 rounded-lg cursor-default transition-all duration-500 ${
                  loc.highlight
                    ? 'bg-[#E71C25]/10 border border-[#E71C25]/25'
                    : 'bg-[#1A1A2E]/50 border border-white/5 hover:border-white/10'
                } ${isInView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
                style={{ transitionDelay: `${300 + i * 80}ms` }}
                onMouseEnter={() => setActivePin(i)}
                onMouseLeave={() => setActivePin(null)}
              >
                <div className="relative flex-shrink-0">
                  {loc.highlight && (
                    <div className="absolute -inset-1 rounded-full bg-[#E71C25]/20 animate-ping" />
                  )}
                  <div
                    className={`w-3 h-3 rounded-full ${
                      loc.highlight ? 'bg-[#E71C25]' : 'bg-white/30'
                    }`}
                  />
                </div>
                <div className="min-w-0">
                  <p className={`font-semibold text-sm ${loc.highlight ? 'text-white' : 'text-white/80'}`}>
                    {loc.name}
                  </p>
                  <p className="text-[#B0B0B0] text-xs mt-0.5">{loc.label}</p>
                </div>
                {loc.highlight && (
                  <span className="ml-auto text-[#E71C25] text-[10px] font-bold tracking-widest uppercase flex-shrink-0">
                    HQ
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
